import { label } from '@entities/ksp/format';
import type { KspWorkspace } from '@entities/ksp/types';
import type { MtoRun, MtoRunDetail } from '@entities/mto/types';

export interface UseWorkspaceOptionsParams {
  mtoRuns: MtoRun[];
  latestMtoRunData?: MtoRunDetail;
  fieldSites: Array<{
    id: string;
    name: string;
    titul_id?: string | null;
    titul_code?: string | null;
    titul_name?: string | null;
  }>;
  data?: KspWorkspace;
}

export function useWorkspaceOptions({
  mtoRuns,
  latestMtoRunData,
  fieldSites,
  data,
}: UseWorkspaceOptionsParams) {
  const mtoRunOptions = mtoRuns.map((r) => ({
    value: r.id,
    label: `${r.summary?.trim() || r.module || ''} (${r.created_at.slice(0, 10)})`,
  }));

  const mtoRowOptions = [
    ...(latestMtoRunData?.materials ?? []).map((m) => ({
      value: m.id,
      label: `[Потр.] ${m.name} — ${m.quantity ?? 0} ${m.unit ?? ''}`,
    })),
    ...(latestMtoRunData?.orders ?? []).map((o) => ({
      value: o.id,
      label: `[Заказ ${o.order_no ?? 'б/н'}] ${o.position_name} — ${o.quantity ?? 0} ${o.unit ?? ''}`,
    })),
    ...(latestMtoRunData?.deliveries ?? []).map((dl) => ({
      value: dl.id,
      label: `[Приход ${dl.order_no ?? 'б/н'}] ${dl.position_name} — ${dl.quantity ?? 0} ${dl.unit ?? ''}`,
    })),
  ];

  const fieldSiteOptions = fieldSites.map((s) => ({
    value: s.id,
    label: `${s.titul_id ? `${s.titul_code ?? ''} — ${s.titul_name ?? 'Титул'}` : 'Не распределено по титулам'} / ${s.name}`,
  }));

  const workOptions =
    data?.activities?.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` })) ?? [];

  const calendarOptions =
    data?.calendars?.map((c) => ({
      value: c.id,
      label: c.name ?? `${c.timezone} · версия ${c.version}`,
    })) ?? [];

  const wbsOptions =
    data?.wbs?.map((node) => ({ value: node.id, label: `${node.code} ${node.name}` })) ?? [];

  const letterOptions =
    data?.letters?.map((letter) => ({
      value: letter.id,
      label: `${letter.letter_no ?? 'Без номера'} · ${letter.reasons.slice(0, 80)}`,
    })) ?? [];

  const evidenceOptions =
    data?.evidence
      ?.filter((evidence) => !evidence.revoked_at)
      .map((evidence) => ({
        value: evidence.id,
        label: `${evidence.channel} · ${evidence.reference} · ${evidence.dispatched_at}`,
      })) ?? [];

  const fileOptions =
    data?.source_files?.map((file) => ({ value: file.id, label: file.filename })) ??
    data?.imports?.map((i) => ({ value: i.source_file_id ?? i.id, label: i.original_name })) ??
    [];

  const exportObjects: { value: string; label: string; kind: string; snapshot_id?: string }[] = [
    ...(data?.period_plans ?? []).map((plan) => ({
      value: plan.id,
      kind: 'period_plan',
      snapshot_id: plan.snapshot_id,
      label: `МСГ ${plan.period_start} — ${plan.period_finish}, ревизия ${plan.revision}, ${label(plan.status)}`,
    })),
    ...(data?.documents ?? [])
      .filter((doc) => doc.kind === 'deviation_act' || doc.kind === 'recovery_plan')
      .map((doc) => ({
        value: doc.id,
        kind: doc.kind,
        snapshot_id: doc.snapshot_id,
        label: `${label(doc.kind)}: ${doc.title}`,
      })),
    ...(data?.letters ?? []).map((letter) => ({
      value: letter.id,
      kind: 'letter',
      label: `Письмо ${letter.letter_no ?? letter.id.slice(0, 8)}`,
    })),
  ];

  return {
    mtoRunOptions,
    mtoRowOptions,
    fieldSiteOptions,
    workOptions,
    calendarOptions,
    wbsOptions,
    letterOptions,
    evidenceOptions,
    fileOptions,
    exportObjects,
  };
}
